import { handleResponseError } from '@/Helpers.js';
import Group from '@/models/Group.js';
import Model from '@/models/Model.js';
import ModelRole from '@/models/ModelRole.js';
import PageFolder from '@/models/pages/PageFolder.js';
import Role from '@/models/Role.js';
import Tag from '@/models/Tag.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default class Page extends Model {
    static entity = 'pages';

    static modelName = 'Page';

    static includes = ['tags', 'groups', 'icon'];

    static timer = import.meta.env.VITE_APP_DEBUG === 'true' ? 500 : 2000;

    static queryBuilder(slug = null) {
        const param = slug ? `/${slug}` : '';
        return new QueryBuilder(`edit-pages${param}`).include(this.includes);
    }

    static slugValidation = {
        max: 191,
        // eslint-disable-next-line security/detect-unsafe-regex
        regex: /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    };

    static fields() {
        return {
            id: this.attr(null),
            page_folder_id: this.attr(null),
            icon_id: this.attr(null),
            title: this.string(''),
            slug: this.string(''),
            redirect_url: this.string(null, value => (value === 'null' ? null : value)),
            iframe_url: this.string(null, value => (value === 'null' ? null : value)),
            body: this.string(null, value => (value === 'null' ? null : value)),
            order: this.number(1),
            is_public: this.boolean(false),
            published_by: this.attr(null),
            published_at: this.attr(null),

            ...super.fields(),

            media: this.hasMany('medias', 'model_id'),
            groups: this.belongsToMany('groups', 'page_group', 'page_id', 'group_id'),
            view_groups: this.belongsToMany('groups', 'page_view_group', 'page_id', 'group_id'),
            roles: this.morphToMany('roles', 'model_has_roles', 'role_id', 'model_id', 'model_type'),
            tags: this.morphToMany('tags', 'taggables', 'tag_id', 'taggable_id', 'taggable_type'),
            page_folder: this.belongsTo('page_folders', 'page_folder_id'),
            icon: this.belongsTo('icons', 'icon_id'),
        };
    }

    get short_body() {
        return `${this.body.substring(0, 100)}...`;
    }

    static async fetch(folderId = null, search = null, tags = null, groups = null) {
        let endpoint = this.queryBuilder();

        endpoint = folderId ? endpoint.filter('page_folder_id', folderId) : endpoint;

        endpoint = search ? endpoint.raw({ search }) : endpoint;

        endpoint = tags ? endpoint.raw({ tags }) : endpoint;

        endpoint = groups ? endpoint.raw({ groups }) : endpoint;

        const { response } = await this.api().get(endpoint.toUrl(), {
            persistOptions: {
                insert: ['taggables'],
            },
        });

        return response.data;
    }

    static async fetchOne(param) {
        const endpoint = this.queryBuilder(param).include(['roles', 'media', 'viewGroups', 'pageFolder']).toUrl();

        try {
            const { response } = await this.api().get(endpoint, {
                persistOptions: {
                    create: ['model_has_roles', 'taggables'],
                },
                errorHandle: false,
            });

            return response.data;
        } catch (error) {
            return error.response;
        }
    }

    static async createModel(model) {
        if (!model.order) model.order = 1;

        const { response } = await this.api().post(this.queryBuilder().toUrl(), model);

        return response.data;
    }

    static async updateModel(slug, model) {
        const endpoint = this.queryBuilder(slug).toUrl();

        const { response } = await this.api().put(endpoint, model, {
            persistOptions: {
                create: ['model_has_roles', 'taggables'],
            },
        });

        const { roles } = response.data.data;

        if (roles && roles.length === 0) {
            await ModelRole.deleteAll();
        }

        return response.data;
    }

    static async deleteModel(model) {
        const modelId = model.map(page => page.id);

        const { response } = await this.api().delete(this.queryBuilder().toUrl(), {
            save: false,
            data: {
                ids: modelId,
            },
        });

        if (response.status === 200) {
            modelId.forEach(id => {
                Page.delete(id);
            });
        }

        return response.data;
    }

    static getAssignableRoles() {
        return Role.query()
            .where('name', 'Instruction Admin')
            .orWhere('name', 'Content Editor')
            .orWhere('name', 'Instructor')
            .orWhere('name', 'Teaching Assistant')
            .orWhere('name', 'Student')
            .orWhere('name', 'Technical Writer')
            .get();
    }

    static getPageAuthorizedRoleIds() {
        const roles = Role.query()
            .where('name', 'Instruction Admin')
            .orWhere('name', 'Admin')
            .orWhere('name', 'Page Editor')
            .orWhere('name', 'Owner')
            .get();

        return roles.map(role => role.id);
    }

    static async fetchAssociates() {
        return Promise.all([PageFolder.fetch(), Group.fetch(), Tag.fetch('/api/tags?type=page'), Role.fetch()]);
    }

    static async fetchPageMenu() {
        try {
            const { data } = await axios.get('/api/page-menus', {
                errorHandle: false,
            });

            await this.store().dispatch('Menu/setStaticMenu', { menu: data.data, sidebar: 'pages-sidebar' });

            return data.data;
        } catch (e) {
            handleResponseError(e);

            return [];
        }
    }

    static async fetchEditPageMenu() {
        try {
            const { data } = await axios.get('/api/edit-page-menus', {
                errorHandle: false,
            });

            this.store().commit('SET_SIDEBAR_ITEMS', data.data);

            return data.data;
        } catch (e) {
            handleResponseError(e);

            return [];
        }
    }
}
